import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getNotifications, markAllNotificationsRead, setNotificationRead } from "../api";
import VestaSelect from "../components/common/VestaSelect";
import "../assets/css/windows.css";
import "../assets/css/notifications.css";

const PAGE_SIZE = 50;
const NOTIFICATIONS_CHANGED_EVENT = "vesta:notifications-changed";

const CATEGORY_LABELS = {
  CHECKIN_DATA: "Check-in",
  ARRIVAL: "Arrivi",
  CHECKOUT: "Check-out",
  ACCOUNT: "Soggiorni",
  ROOM_STATUS: "Camere",
  MAINTENANCE: "Manutenzioni",
  QUOTE: "Preventivi",
  FISCAL_DOCUMENT: "Documenti fiscali",
  HOTEL: "Hotel",
};

const LEVEL_LABELS = {
  INFO: "Info",
  SUCCESS: "Completata",
  WARNING: "Attenzione",
  DANGER: "Urgente",
  ERROR: "Errore",
};

function cleanText(value) {
  return String(value || "").trim();
}

function formatDateTime(value) {
  const raw = cleanText(value);
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()} ${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
}

function formatDate(value) {
  const raw = cleanText(value);
  if (!raw) return "-";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return raw;
  return `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
}

function normalizeLabel(value, fallback = "Hotel") {
  const text = cleanText(value).toUpperCase();
  if (!text) return fallback;
  return CATEGORY_LABELS[text] || text.charAt(0) + text.slice(1).toLowerCase().replaceAll("_", " ");
}

function levelLabel(value) {
  const level = cleanText(value || "INFO").toUpperCase();
  return LEVEL_LABELS[level] || normalizeLabel(level, "Info");
}

function getNotificationErrorMessage(error) {
  const status = error?.response?.status;
  if (status === 401) return "Sessione scaduta: effettua nuovamente il login";
  if (status === 403) return "Notifiche non abilitate per questo utente";
  if (status === 404) return "API notifiche non trovata: aggiorna e riavvia il backend";
  if (status >= 500) return "Errore server notifiche: controlla il terminale backend";
  return "Impossibile caricare le notifiche";
}

function NotificationCard({ notification, onOpenTarget, onToggleRead, busy }) {
  const targetPath = cleanText(notification?.targetPath);
  const level = cleanText(notification?.level || "INFO").toLowerCase();
  const eventAt = cleanText(notification?.eventAt);
  return (
    <article className={`notifications_card ${notification?.read ? "is_read" : "is_unread"}`}>
      <div className={`notifications_level level_${level}`} aria-hidden="true" />
      <div className="notifications_card_body">
        <div className="notifications_card_head">
          <strong>{cleanText(notification?.title) || "Notifica"}</strong>
          <span>{formatDateTime(notification?.createdAt)}</span>
        </div>
        {notification?.message && <p>{notification.message}</p>}
        {eventAt && <div className="notifications_event_date">{cleanText(notification?.eventLabel) || "Evento"}: <strong>{formatDate(eventAt)}</strong></div>}
        <div className="notifications_meta_row">
          <span>{normalizeLabel(notification?.category || notification?.scope)}</span>
          <span>{levelLabel(notification?.level)}</span>
          <span>{notification?.read ? "Letta" : "Da leggere"}</span>
        </div>
      </div>
      <div className="notifications_card_actions">
        <button
          type="button"
          className="notifications_secondary_button"
          disabled={busy}
          onClick={() => onToggleRead(notification, !notification?.read)}
        >
          {notification?.read ? "Segna da leggere" : "Segna letta"}
        </button>
        {targetPath && (
          <button type="button" className="notifications_open_button" disabled={busy} onClick={() => onOpenTarget(notification)}>
            Apri
          </button>
        )}
      </div>
    </article>
  );
}

export default function Notifications() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ total: 0, unread: 0, attention: 0, categories: [] });
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [markAllBusy, setMarkAllBusy] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [readFilter, setReadFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const loadingRef = useRef(false);
  const requestSequenceRef = useRef(0);
  const itemsCountRef = useRef(0);
  const boardRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchInput.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const loadNotifications = useCallback(async ({ reset = false, silent = false } = {}) => {
    if (loadingRef.current && !reset) return;
    loadingRef.current = true;
    const requestSequence = ++requestSequenceRef.current;
    if (!silent) {
      if (reset) setLoading(true);
      else setLoadingMore(true);
    }
    setError("");

    try {
      const skip = reset ? 0 : itemsCountRef.current;
      const data = await getNotifications({
        limit: PAGE_SIZE,
        skip,
        q: searchQuery || undefined,
        read: readFilter || undefined,
        level: levelFilter || undefined,
        category: categoryFilter || undefined,
      });
      if (requestSequence !== requestSequenceRef.current) return;
      const nextItems = Array.isArray(data?.items) ? data.items : [];
      setItems((current) => {
        const merged = reset ? nextItems : [...current, ...nextItems];
        itemsCountRef.current = merged.length;
        return merged;
      });
      setTotal(Number(data?.total || 0));
      setStats({
        total: Number(data?.stats?.total || 0),
        unread: Number(data?.stats?.unread || 0),
        attention: Number(data?.stats?.attention || 0),
        categories: Array.isArray(data?.stats?.categories) ? data.stats.categories : [],
      });
      setHasMore(Boolean(data?.hasMore));
    } catch (loadError) {
      if (requestSequence !== requestSequenceRef.current) return;
      setError(getNotificationErrorMessage(loadError));
      if (reset) {
        itemsCountRef.current = 0;
        setItems([]);
        setTotal(0);
        setHasMore(false);
      }
    } finally {
      if (requestSequence === requestSequenceRef.current) {
        loadingRef.current = false;
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [categoryFilter, levelFilter, readFilter, searchQuery]);

  useEffect(() => {
    if (boardRef.current) boardRef.current.scrollTop = 0;
    loadNotifications({ reset: true });
  }, [loadNotifications]);

  useEffect(() => {
    const timer = window.setInterval(() => loadNotifications({ reset: true, silent: true }), 60000);
    return () => window.clearInterval(timer);
  }, [loadNotifications]);

  const categoryOptions = useMemo(() => [
    { value: "", label: "Tutte le categorie" },
    ...stats.categories.map((category) => ({ value: category, label: normalizeLabel(category) })),
  ], [stats.categories]);

  const handleScroll = (event) => {
    if (!hasMore || loadingRef.current) return;
    const element = event.currentTarget;
    const bottomDistance = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (bottomDistance <= 120) loadNotifications({ reset: false });
  };

  const refreshNotifications = () => {
    if (boardRef.current) boardRef.current.scrollTop = 0;
    loadNotifications({ reset: true });
  };

  const resetFilters = () => {
    setSearchInput("");
    setSearchQuery("");
    setReadFilter("");
    setLevelFilter("");
    setCategoryFilter("");
  };

  const updateReadState = async (notification, read) => {
    const id = cleanText(notification?._id);
    if (!id || busyId) return;
    setBusyId(id);
    setError("");
    try {
      await setNotificationRead(id, read);
      setItems((current) => current.map((item) => String(item._id) === id ? { ...item, read } : item));
      setStats((current) => ({ ...current, unread: Math.max(0, current.unread + (read ? -1 : 1)) }));
      if (readFilter) await loadNotifications({ reset: true, silent: true });
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
    } catch (updateError) {
      setError(getNotificationErrorMessage(updateError));
    } finally {
      setBusyId("");
    }
  };

  const openTarget = async (notification) => {
    if (!notification?.read) await updateReadState(notification, true);
    if (notification?.targetPath) navigate(notification.targetPath);
  };

  const markAllRead = async () => {
    if (markAllBusy || stats.unread <= 0) return;
    setMarkAllBusy(true);
    setError("");
    try {
      await markAllNotificationsRead();
      setItems((current) => current.map((item) => ({ ...item, read: true })));
      setStats((current) => ({ ...current, unread: 0 }));
      if (readFilter === "unread") await loadNotifications({ reset: true, silent: true });
      window.dispatchEvent(new Event(NOTIFICATIONS_CHANGED_EVENT));
    } catch (updateError) {
      setError(getNotificationErrorMessage(updateError));
    } finally {
      setMarkAllBusy(false);
    }
  };

  return (
    <section className="window_page notifications_page">
      <div className="window_banner">
        <div className="window_banner_copy">
          <h1>NOTIFICHE</h1>
          <p>Centro operativo per avvisi personali e dell'hotel: priorità, scadenze, arrivi, soggiorni, camere, preventivi e documenti fiscali.</p>
        </div>
      </div>

      <div className="notifications_summary" aria-label="Riepilogo notifiche">
        <div className="notifications_summary_card"><span>Totali</span><strong>{stats.total}</strong></div>
        <div className="notifications_summary_card"><span>Da leggere</span><strong>{stats.unread}</strong></div>
        <div className="notifications_summary_card"><span>Prioritarie</span><strong>{stats.attention}</strong></div>
      </div>

      <div className="notifications_panel">
        <div className="notifications_toolbar">
          <div className="notifications_filters">
            <input
              type="search"
              className="notifications_search"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Cerca nelle notifiche"
              aria-label="Cerca nelle notifiche"
              autoComplete="off"
            />
            <VestaSelect value={readFilter} onValueChange={setReadFilter} ariaLabel="Filtra notifiche per stato" options={[
              { value: "", label: "Tutte" },
              { value: "unread", label: "Da leggere" },
              { value: "read", label: "Lette" },
            ]} />
            <VestaSelect value={levelFilter} onValueChange={setLevelFilter} ariaLabel="Filtra notifiche per priorità" options={[
              { value: "", label: "Tutte le priorità" },
              { value: "DANGER", label: "Urgente" },
              { value: "WARNING", label: "Attenzione" },
              { value: "INFO", label: "Info" },
              { value: "SUCCESS", label: "Completata" },
            ]} />
            <VestaSelect value={categoryFilter} onValueChange={setCategoryFilter} ariaLabel="Filtra notifiche per categoria" options={categoryOptions} />
          </div>
          <div className="notifications_toolbar_actions">
            <button type="button" className="notifications_secondary_button" onClick={resetFilters}>Azzera filtri</button>
            <button type="button" className="notifications_secondary_button" disabled={markAllBusy || stats.unread <= 0} onClick={markAllRead}>{markAllBusy ? "Aggiornamento..." : "Segna tutte lette"}</button>
            <button type="button" className="notifications_refresh_button" onClick={refreshNotifications}>Aggiorna</button>
          </div>
          <div className="notifications_toolbar_copy">
            <strong>Risultati: {total}</strong>
            <span>{items.length} visualizzate</span>
          </div>
        </div>

        <div className="notifications_board" ref={boardRef} onScroll={handleScroll}>
          {loading && <div className="notifications_status notifications_loading_status" role="status" aria-live="polite"><span className="notifications_loading_spinner" aria-hidden="true"><span /></span><span>Caricamento notifiche...</span></div>}
          {!loading && error && <div className="notifications_status is_error">{error}</div>}
          {!loading && !error && items.length === 0 && <div className="notifications_status">Nessuna Notifica Corrispondente</div>}

          {!loading && items.length > 0 && (
            <div className="notifications_list">
              {items.map((notification) => (
                <NotificationCard
                  key={notification._id}
                  notification={notification}
                  busy={busyId === String(notification._id)}
                  onOpenTarget={openTarget}
                  onToggleRead={updateReadState}
                />
              ))}
              {loadingMore && <div className="notifications_status is_inline notifications_loading_inline" role="status" aria-live="polite"><span className="notifications_loading_spinner is_small" aria-hidden="true"><span /></span><span>Caricamento altre notifiche...</span></div>}
              {!loadingMore && !hasMore && <div className="notifications_status is_inline">Fine elenco notifiche</div>}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
